import type { FormModelFieldSubForm } from '@ikegps/core-shared';
import { FormDesignerPalette, isFieldTypeAllowed } from '../../form.palette';
import { FormFieldEditor } from './../form.field.editor';
import { FormFieldTreeView } from './../form.field.tree.view';
import { m, type MithrilVNode } from '../../../../mithril';
import { type SelectInputOption, SelectInputState } from '../../../../components/input/input.select';
import type { MenuComponent } from '../../../../components/menu/menu';
import type { FormDesigner } from '../../form.designer';
import { T, Translate } from '../../../../i18n';
import { IkeFieldType, SubFormType } from '@ikegps/core-api';
import { SpanSubFormTypeEditor } from './form.field.editor.subform.span';
import { AnchorSubFormTypeEditor } from './form.field.editor.subform.anchor';
import { SubformEditorDropdown } from './form.field.editor.subform.dropdown';
import { createMenu } from './form.field.editor.subform.type';
import { EquipmentSubFormTypeEditor } from './form.field.editor.subform.equipment';

export class FormFieldEditorSubForm extends FormFieldEditor<FormModelFieldSubForm> {
	private subFormTypeSelectState: SelectInputState<SubFormType>;
	private subFormTypeMenu: MenuComponent;
	private typeEditor: SpanSubFormTypeEditor | AnchorSubFormTypeEditor | EquipmentSubFormTypeEditor | null = null;

	constructor(designer: FormDesigner, field: FormModelFieldSubForm) {
		super(designer, field);

		this.subFormTypeSelectState = this.initialSubFormTypeSelectState();
		this.subFormTypeMenu = createMenu(this.subFormTypeSelectState);

		// If current type is SPAN, create the span type editor
		if (this.subFormTypeSelectState.value === SubFormType.SPAN) {
			this.typeEditor = new SpanSubFormTypeEditor(this.field, this.state, this.designer);
		}

		if (this.subFormTypeSelectState.value === SubFormType.ANCHOR) {
			this.typeEditor = new AnchorSubFormTypeEditor(this.field, this.state);
		}

		if (this.subFormTypeSelectState.value === SubFormType.EQUIPMENT) {
			this.typeEditor = new EquipmentSubFormTypeEditor(this.field, this.state);
		}

		// This handler responds to USER changes of the subform type.
		this.subFormTypeSelectState.onValue((subformTypeValue) => {
			this.typeEditor?.destroy();
			this.field.Mutator.span.disable();
			this.field.Mutator.anchor.disable();
			this.field.Mutator.equipment.disable();

			switch (subformTypeValue) {
				case SubFormType.SPAN:
					this.field.Mutator.span.enable(isFieldTypeAllowed(IkeFieldType.MID_SPAN));
					this.typeEditor = new SpanSubFormTypeEditor(this.field, this.state, this.designer);
					break;
				case SubFormType.ANCHOR:
					this.field.Mutator.anchor.enable();
					this.typeEditor = new AnchorSubFormTypeEditor(this.field, this.state);
					break;
				case SubFormType.EQUIPMENT: {
					this.field.Mutator.equipment.enable();
					const typeFieldId = this.field.subFormEquipmentState?.typeFieldId;
					if (typeFieldId) {
						this.designer.clearCachedEditor(this.field.id + ':' + typeFieldId);
					}
					this.typeEditor = new EquipmentSubFormTypeEditor(this.field, this.state);
					break;
				}
				default:
					this.typeEditor = null;
			}

			this.designer
				.getSubFormEditors()
				.filter((e) => e !== this)
				.forEach((e) => e.refreshOptions());
		});
	}

	private initialSubFormTypeSelectState(): SelectInputState<SubFormType> {
		let options: SelectInputOption<SubFormType>[] = [];

		options = this.getSubFormTypeOptions() ?? [];

		const state = new SelectInputState(Translate(T.TYPE), options);
		state.placeholder = Translate(T.NONE);
		state.setValue(this.field.subFormType);

		this.state.add(state);
		state.isLabelShown = true;

		return state;
	}

	refreshOptions() {
		const newOptions = this.getSubFormTypeOptions() ?? [];
		this.subFormTypeSelectState.setOptions(newOptions);
	}

	private getSubFormTypeOptions(): SelectInputOption<SubFormType>[] | null {
		const isTopLevelSubform = this.field.isRoot || false;
		if (!isTopLevelSubform) {
			return null;
		}

		return Object.values(SubFormType)
			.filter((subformType) => subformType !== SubFormType.NONE && subformType !== SubFormType.POLE)
			.filter((subformType) => {
				const typedSubForm = this.designer.form?.getTypedSubForm(subformType);
				return typedSubForm ? typedSubForm.id === this.field.id : true;
			})
			.map((subformType) => ({
				key: subformType.charAt(0).toUpperCase() + subformType.slice(1).toLowerCase(),
				value: subformType,
			}));
	}

	override destroy() {
		this.typeEditor?.destroy();
		this.typeEditor = null;
		super.destroy();
	}

	override viewTree() {
		return FormFieldTreeView.viewForm(this);
	}

	override viewInfoRW() {
		return m('div', {}, [
			this.viewSubFormTypeEditor(),
			FormDesignerPalette.view((type) => this.designer.createField(this.field, type), this.depth, this.field.getForm()),
		]);
	}

	private viewSubFormTypeEditor(): MithrilVNode | null {
		const options = this.getSubFormTypeOptions();
		if (!options || options.length === 0) {
			return null;
		}
		const selectedOpt = this.subFormTypeSelectState.options.find((o) => o.value === this.subFormTypeSelectState.value);
		const selectedText = selectedOpt?.key ?? this.subFormTypeSelectState.placeholder ?? Translate(T.SELECT_OPTION);
		return m('div', { style: { 'margin-bottom': '32px', display: 'flex', 'flex-direction': 'column' } }, [
			SubformEditorDropdown.view({
				title: Translate(T.TYPE),
				selectedText,
				menu: this.subFormTypeMenu,
			}),
			...(this.typeEditor ? [this.typeEditor.view()] : []),
		]);
	}
}
